import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Autocomplete } from "@mui/lab";
import { Grid, TextField, Button } from '@mui/material';
import { ButtonAdd } from './general/mini_components';
import { Billable } from '../pages/hospital/Billing/types';
import { TYPE_BILL_ITEM } from '../constants/types';
import { ReduxStore } from '../constants/types';
import { pushBillables } from '../redux/actions/billingActions';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';

/**
 * PackageBillableSelector Component
 * 
 * A component that allows users to select billables of type "package" and add them
 * to the current bill with a specified quantity. The component only renders when
 * package billables are available.
 * 
 * Features:
 * - Filters billables to show only package types (TYPE_BILL_ITEM.PACKAGE)
 * - Quantity validation (1-50)
 * - Conditional rendering (doesn't render if no packages available)
 * - Redux integration for adding billables to the current bill
 * 
 * Usage:
 * <PackageBillableSelector />
 */
export const PackageBillableSelector: React.FC<LocalizeContextProps> = (props) => {
    const dispatch = useDispatch();
    const billables = useSelector((state: ReduxStore) => 
        state.billing.data.billablesCurrentBill ? state.billing.data.billablesCurrentBill : []
    );
    const [selectedBillable, setSelectedBillable] = useState<Billable | null>(null);
    const [quantity, setQuantity] = useState<number>(1);
    const [error, setError] = useState<string>('');

    // Filter billables to only show packages
    const packageBillables = billables.filter(billable => billable.type === TYPE_BILL_ITEM.PACKAGE);

    // Don't render if no package billables are available
    if (packageBillables.length === 0) {
        return null;
    }

    const validateQuantity = (value: number): boolean => {
        if (value <= 0) {
            setError('Quantity must be greater than 0');
            return false;
        }
        if (value > 50) {
            setError('Quantity cannot exceed 50');
            return false;
        }
        setError('');
        return true;
    };

    const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value) || 0;
        setQuantity(value);
        validateQuantity(value);
    };

    const handleAddBillable = () => {
        if (!selectedBillable) {
            setError('Please select a package');
            return;
        }

        if (validateQuantity(quantity)) {
            // Create multiple billable items based on quantity
            // Each item will get its own package number when processed by the reducer
            const billablesToAdd = Array(quantity).fill({ id: selectedBillable.id });
            dispatch(pushBillables(billablesToAdd));
            setSelectedBillable(null);
            setQuantity(1);
            setError('');
        }
    };

    const handleBillableSelect = (billable: Billable | null) => {
        setSelectedBillable(billable);
        setError('');
    };

    return (
        <Grid container xs={12} style={{ display: 'flex', paddingTop: '1rem' }}>
            <Grid container item xs={12} style={{ display: 'flex', alignItems: 'center' }}>
                <Autocomplete
                    value={selectedBillable}
                    options={packageBillables}
                    onChange={(_, newValue) => handleBillableSelect(newValue)}
                    getOptionLabel={(option) => option.concept}
                    style={{ width: 300 }}
                    renderInput={(params) => 
                        <TextField 
                            {...params} 
                            label={<Translate id="hospital.billing.bill.selectors.select_package" />}
                            error={!!error}
                            helperText={error}
                            color="secondary"
                            variant="outlined" 
                        />
                    }
                />
                <TextField
                    type="number"
                    label={<Translate id="hospital.billing.bill.selectors.quantity" />}
                    value={quantity}
                    onChange={handleQuantityChange}
                    inputProps={{ min: 1, max: 50 }}
                    style={{ width: 120, marginLeft: '0.5rem' }}
                    color="secondary"
                    variant="outlined"
                    error={!!error}
                />
                {selectedBillable && (
                    <ButtonAdd 
                        style={{ marginLeft: '0.5rem' }} 
                        onClick={handleAddBillable}
                        disabled={!!error || quantity <= 0 || quantity > 50}
                    />
                )}
            </Grid>
        </Grid>
    );
};

export default withLocalize(PackageBillableSelector); 